import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { provideAuth0 } from '@auth0/auth0-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { transientRetryInterceptor } from './core/interceptors/transient-retry.interceptor';
import { pageLoaderInterceptor } from './core/interceptors/page-loader.interceptor';
import { StaleBundleErrorHandler } from './core/errors/stale-bundle-error.handler';
import { MOCK_INTERCEPTORS, MOCK_PROVIDERS } from './core/mock/mock-providers';

// No preloading strategy passed to provideRouter (Angular default = NoPreloading): lazy chunks
// load on demand instead of eagerly downloading every feature (admin/editor/analytics — incl.
// echarts/html2canvas) right after the app becomes stable. Tradeoff: first navigation into a
// feature pays a one-time chunk fetch, but initial load and bandwidth drop significantly. This is
// carried over unchanged from the pre-standalone AppRoutingModule, which likewise passed no
// preloading strategy to RouterModule.forRoot.
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      IonicModule.forRoot({
        mode: 'md',
        innerHTMLTemplatesEnabled: true,
        useSetInputAPI: true,
      }),
      FormsModule,
    ),
    provideRouter(routes),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // pageLoaderInterceptor runs outermost so the global loader spans the whole request
    // lifecycle — including retries and token acquisition — instead of flickering between
    // them. transientRetryInterceptor is registered before authInterceptor so a retried
    // attempt runs through token acquisition again — a retry after an instance handover must
    // not replay a token that expired meanwhile.
    // The mock API (core/mock, empty in every other build) answers last, so the auth interceptor still sees its errors.
    provideHttpClient(withInterceptors([pageLoaderInterceptor, transientRetryInterceptor, authInterceptor, ...MOCK_INTERCEPTORS])),
    provideAuth0({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri:
          environment.auth0RedirectUri || `${window.location.origin}/callback`,
        audience: environment.auth0Audience || undefined,
      },
      // Persist login across reloads and enable refresh tokens.
      // SECURITY: cacheLocation 'localstorage' keeps the Auth0 token cache (incl. the rotating
      // refresh token) in localStorage, which is readable by any same-origin JS and therefore
      // exposed to XSS token theft. The hardened alternative is cacheLocation 'memory' +
      // useRefreshTokens: true + useRefreshTokensFallback: false.
      // NOT switched here on purpose: with 'memory' the cache (and the refresh token) is dropped on
      // every page reload, so re-auth then depends on the Auth0 iframe silent-auth fallback, which
      // requires third-party cookies (blocked by Safari ITP / the Chrome 3p-cookie phase-out). With
      // useRefreshTokensFallback: false there is no recovery, so users would be logged out on every
      // refresh. Enabling 'memory' safely needs verified Auth0-tenant settings (refresh-token
      // rotation + reuse detection, and confirming the silent-auth path is not relied on) that
      // cannot be validated from the SPA repo alone. Revisit once the tenant config is confirmed.
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: ErrorHandler, useClass: StaleBundleErrorHandler },
    // Fake sign-in and sample data of the mock mode, after provideAuth0 so it replaces Auth0 (empty in every other build).
    ...MOCK_PROVIDERS,
  ],
};
